import { cn } from "@suns/design-system/src/utils/cn";

import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@suns/design-system/src/components/Chart/Chart";

import {
  generateChartTicks,
  uniqueTicks,
  xTicksFormatter,
} from "@/shared/utils/helper-functions";
import { apolloGradeColor } from "@/shared/utils/colors";

import { roundToSignificantDigit } from "@/shared/utils/helper-functions";
import { ChartConfig } from "@suns/design-system";

import {
  LineChart,
  YAxis,
  CartesianGrid,
  XAxis,
  ReferenceLine,
  Line,
  Dot,
} from "recharts";
import { theme } from "@/shared/theme";
import { Timeframe } from "@/shared/const";

export interface ChartData {
  snapshotDate: string;
  [key: string]: string | number | null;
}

export function MultiLineChart({
  chartData,
  chartConfig,
  activeChart,
  chartType,
  yAxisConfig,
  useApolloGradeColors = false,
  timeframe,
}: {
  chartData: ChartData[];
  chartConfig: ChartConfig;
  activeChart: keyof typeof chartConfig | null;
  chartType?: "currency" | null;
  yAxisConfig?: {
    minValue?: number;
    maxValue?: number;
    minTickCount?: number;
    customTick?:
      | React.ReactElement
      | ((props: {
          x: number;
          y: number;
          payload: { value: number };
        }) => React.ReactElement)
      | undefined;
  };
  useApolloGradeColors?: boolean;
  timeframe: Timeframe;
}) {
  const allChartValues = chartData.flatMap((d) =>
    Object.keys(chartConfig).map((key) => Number(d[key]))
  );

  const minTicksValue = Math.min(
    0,
    roundToSignificantDigit(
      Math.min(...allChartValues) > 0
        ? Math.min(...allChartValues)
        : Math.min(...allChartValues) * 1.5
    )
  );

  const maxTicksValue = roundToSignificantDigit(
    Math.max(...allChartValues) * 1.5
  );

  const ticks = generateChartTicks(
    yAxisConfig?.minValue ?? minTicksValue,
    yAxisConfig?.maxValue ?? maxTicksValue,
    yAxisConfig?.minTickCount ?? 8
  );

  return (
    <ChartContainer config={chartConfig} className="h-[400px] w-full md:h-full">
      <LineChart
        accessibilityLayer
        data={chartData}
        margin={{
          top: 12,
          left: 20,
          right: 12,
        }}
      >
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="snapshotDate"
          tickMargin={6}
          tickFormatter={(value) => {
            return xTicksFormatter(value, timeframe);
          }}
          ticks={uniqueTicks(chartData, timeframe)}
        />
        <YAxis
          width={chartType !== "currency" ? 10 : 30}
          interval={0}
          dataKey={Object.keys(chartConfig)[0]}
          type="number"
          domain={[
            yAxisConfig?.minValue ?? minTicksValue,
            yAxisConfig?.maxValue ?? maxTicksValue,
          ]}
          ticks={ticks}
          tick={yAxisConfig?.customTick ? yAxisConfig?.customTick : true}
          tickFormatter={(value) => {
            if (chartType === "currency") {
              return value.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
                notation: "compact",
                compactDisplay: "short",
              });
            }
            return value;
          }}
          tickLine={false}
        />
        <ReferenceLine
          y={0}
          stroke={theme.colors.gray[800]}
          strokeWidth={1.5}
          strokeOpacity={0.65}
        />

        <ChartTooltip
          content={
            <ChartTooltipContent
              valueFormatter={(value) => {
                if (chartType === "currency") {
                  const USDollar = new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                    maximumFractionDigits: 0,
                  });
                  return USDollar.format(Number(value));
                }
                return value;
              }}
            />
          }
        />

        <ChartLegend content={<ChartLegendContent />} />
        {Object.entries(chartConfig).map(([key, config], index) => (
          <Line
            key={key + index}
            dataKey={key}
            type="monotone"
            stroke={config.color}
            className={cn(activeChart && activeChart !== key && "opacity-40")}
            strokeWidth={2}
            animationDuration={900}
            connectNulls={true}
            activeDot={{ r: 6 }}
            dot={({ payload, ...props }) => {
              const value = payload[key];
              return (
                <Dot
                  className={cn(
                    activeChart && activeChart !== key && "opacity-40"
                  )}
                  key={`${key}-${props.cx}-${props.cy}`}
                  r={4}
                  cx={props.cx}
                  cy={props.cy}
                  fill={
                    useApolloGradeColors
                      ? apolloGradeColor(value, "hex")
                      : config.color
                  }
                  stroke={config.color}
                />
              );
            }}
          />
        ))}
      </LineChart>
    </ChartContainer>
  );
}
